import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, FileText } from "lucide-react";
import { PDFViewer } from "@/components/ui/pdf-viewer";
import { Button } from "@/components/ui/button";

const HOTEL_GUIDE_PROXY_BASE =
  `${(import.meta as any).env?.VITE_SUPABASE_URL || "https://ruamgnhtybcahvtzwtem.supabase.co"}/functions/v1/hotel-guide-proxy`;
const DEFAULT_FILENAME = "OFUESI-2026-Hotel-Accommodation-Guide.pdf";

const HotelAccommodationGuide = () => {
  const [searchParams] = useSearchParams();

  const queryString = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("download");
    params.set("documentType", params.get("documentType") || "hotel-guide");
    if (!params.get("year")) params.set("year", "2026");
    return params.toString();
  }, [searchParams]);

  const viewUrl = `${HOTEL_GUIDE_PROXY_BASE}/view?${queryString}`;
  const downloadUrl = `${HOTEL_GUIDE_PROXY_BASE}/download?${queryString}`;

  useEffect(() => {
    document.title = "Hotel Accommodation Guide – OFUESI";
    const desc = "View or download the OFUESI 2026 Southern Region Hotel Accommodation Guide.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  useEffect(() => {
    if (searchParams.get("download") !== "1") return;
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = DEFAULT_FILENAME;
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, [downloadUrl, searchParams]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-1 rounded-full bg-sky-50 p-2 text-sky-700 ring-1 ring-sky-200">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight text-slate-950 sm:text-2xl">Hotel Accommodation Guide</h1>
              <p className="text-sm text-slate-600">OFUESI 2026 Southern Region Conference</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/conference/southern?tab=register">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Registration
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={viewUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open PDF
              </a>
            </Button>
            <Button asChild size="sm" className="bg-sky-700 text-white hover:bg-sky-800">
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" download={DEFAULT_FILENAME}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-2 py-3 sm:px-4 lg:px-6">
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
          <p className="font-semibold">Important — Hotel Pricing Disclaimer</p>
          <p className="mt-1 leading-relaxed">
            Hotel rates change frequently. The prices listed in this guide are accurate as of <strong>06/15/2026</strong>.
            Please contact the respective hotels directly to confirm the final rates, availability, and any taxes or fees before booking.
          </p>
        </div>
        <div className="h-[calc(100vh-185px)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <PDFViewer url={viewUrl} fileName={DEFAULT_FILENAME} dialogOpen debugLabel="Hotel Accommodation Guide" />
        </div>
      </main>
    </div>
  );
};

export default HotelAccommodationGuide;